/**
 * Dismissible top notification bar — promo / feature / status announcements.
 * Configurable content via /api/site/notification (super admin can update),
 * dismissed-state stored in localStorage keyed by message id so users
 * don't keep seeing the same banner.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Sparkles, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = "mm-notif-dismissed-v1";

export default function NotificationBar() {
  const [bar, setBar] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/api/site/notification`).then((r) => {
      if (cancel) return;
      const data = r.data || {};
      if (!data.enabled || !data.message) return;
      const dismissed = (localStorage.getItem(STORAGE_KEY) || "").split(",");
      if (dismissed.includes(String(data.id))) return;
      setBar(data);
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  if (!bar) return null;

  const dismiss = () => {
    const dismissed = (localStorage.getItem(STORAGE_KEY) || "").split(",").filter(Boolean);
    dismissed.push(String(bar.id));
    localStorage.setItem(STORAGE_KEY, dismissed.slice(-20).join(","));
    setBar(null);
  };

  return (
    <div
      data-testid="top-notification-bar"
      style={{
        position: "relative",
        background: bar.background || "linear-gradient(90deg, #7C3AED 0%, #EC4899 50%, #F97316 100%)",
        color: "#FFFFFF",
        padding: "10px 48px 10px 16px",
        textAlign: "center",
        fontSize: 14,
        fontWeight: 500,
        zIndex: 100,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        <Sparkles size={14}/>
        <strong>{bar.message}</strong>
        {bar.cta_url && bar.cta_label && (
          <a
            href={bar.cta_url}
            data-testid="notif-bar-cta"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 10px",
              background: "rgba(255,255,255,0.18)",
              borderRadius: 20,
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.22)",
            }}
          >
            {bar.cta_label} <ArrowRight size={11}/>
          </a>
        )}
      </span>
      <button
        onClick={dismiss}
        data-testid="notif-bar-close"
        aria-label="Dismiss"
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.12)",
          border: "none",
          color: "#fff",
          width: 26,
          height: 26,
          borderRadius: "50%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <X size={14}/>
      </button>
    </div>
  );
}
